"use client";

import { useState, useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import { onChatCommand } from "../lib/chat-bus";

// Live R3F Jess as the trigger — loaded client-only, lazy, never SSR'd.
const JessAvatar = dynamic(() => import("./experience/JessAvatar"), { ssr: false });

// Loaded dynamically (client-only) to keep it out of the server-rendered HTML.
const ChatPanel = dynamic(() => import("./ChatPanel"), { ssr: false });

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  // Gate render until mounted so SSR and first client paint match (no
  // hydration mismatch), then suppress the widget entirely on the
  // Thermann domain. The Thermann host rewrites internally to /thermann
  // but the browser URL (and hostname) stays thermann.duxai.com.au, so
  // we detect by hostname rather than pathname. Suppressing here means
  // <JessAvatar/> (three.js + /models/jess.glb) never mounts there —
  // which also avoids the GLB load crashing under the proxy rewrite.
  const [enabled, setEnabled] = useState(false);
  const openTriggerRef = useRef<string>("button");

  useEffect(() => {
    setEnabled(!window.location.hostname.includes("thermann"));
  }, []);

  // Subscribe to global chat bus (used by ContactFooter, etc).
  useEffect(() => {
    return onChatCommand((cmd, trigger) => {
      if (cmd === "open") {
        if (trigger) openTriggerRef.current = trigger;
        setOpen(true);
      } else if (cmd === "close") {
        setOpen(false);
      } else if (cmd === "toggle") {
        setOpen((v) => !v);
      }
    });
  }, []);

  // "/" opens chat from anywhere when no text input is focused.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if (e.key === "/" && !inField && !open) {
        e.preventDefault();
        openTriggerRef.current = "keyboard";
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Not mounted yet, or on the Thermann domain → render nothing.
  if (!enabled) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => {
            openTriggerRef.current = "button";
            setOpen(true);
          }}
          aria-label="Open chat with Dux AI Assistant"
          className="chat-trigger"
        >
          <span className="chat-trigger-jess" aria-hidden="true">
            <JessAvatar />
          </span>
        </button>
      )}

      {open && <ChatPanel onClose={() => setOpen(false)} />}
    </>
  );
}
